export const MOUTH_SHAPES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'X'] as const

export type MouthShape = (typeof MOUTH_SHAPES)[number]

export interface MouthCue {
  start: number
  end: number
  value: MouthShape
}

export interface LipSyncTrack {
  duration: number
  mouthCues: MouthCue[]
}

export interface LipSyncPlatformSample {
  frameSeconds: number
  audioOutputLatencySeconds?: number | null
}

export interface LipSyncTimingController {
  readonly visualLeadSeconds: number
  probePlatformLatency(): void
  sampleFrameDuration(seconds: number): void
}

/**
 * Rhubarb cues are sampled from the media clock independently of the display
 * refresh rate.  A 120 Hz cadence keeps a cue boundary from waiting for a
 * dropped render frame while the actor remains driven by the same audio clock.
 */
export const LIP_SYNC_SAMPLE_HZ = 120

const DEFAULT_FRAME_SECONDS = 1 / 60
const DEFAULT_AUDIO_OUTPUT_LATENCY_SECONDS = 0.02
const BASE_VISUAL_LEAD_SECONDS = 0.075
const MIN_VISUAL_LEAD_SECONDS = 0.035
const MAX_VISUAL_LEAD_SECONDS = 0.2

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Advance the mouth enough to cover the next rendered frame and the device output buffer. A media
 * element's currentTime is the playback timeline; direct element audio still has to cross the
 * platform output queue before it reaches the listener. Bounds prevent noisy reports from making
 * animation look implausibly premature.
 */
export function adaptiveLipSyncLeadSeconds(sample: LipSyncPlatformSample): number {
  const frameSeconds = clamp(sample.frameSeconds, 1 / 120, 1 / 30)
  const audioLatency = clamp(
    sample.audioOutputLatencySeconds ?? DEFAULT_AUDIO_OUTPUT_LATENCY_SECONDS,
    0,
    0.1,
  )
  return clamp(BASE_VISUAL_LEAD_SECONDS + frameSeconds * 1.5 + audioLatency, MIN_VISUAL_LEAD_SECONDS, MAX_VISUAL_LEAD_SECONDS)
}

type AudioContextConstructor = new (contextOptions?: AudioContextOptions) => AudioContext

/** Best-effort platform probe; unsupported or blocked browsers use the bounded default. */
export function browserAudioOutputLatencySeconds(): number | null {
  if (typeof window === 'undefined') return null
  const browserWindow = window as typeof window & { webkitAudioContext?: AudioContextConstructor }
  const Context = browserWindow.AudioContext ?? browserWindow.webkitAudioContext
  if (!Context) return null
  try {
    const context = new Context({ latencyHint: 'interactive' })
    const latency = context.outputLatency > 0 ? context.outputLatency : context.baseLatency
    void context.close().catch(() => undefined)
    return Number.isFinite(latency) && latency >= 0 ? latency : null
  } catch {
    return null
  }
}

export class AdaptiveLipSyncTiming implements LipSyncTimingController {
  private frameSeconds = DEFAULT_FRAME_SECONDS
  private audioOutputLatencySeconds = DEFAULT_AUDIO_OUTPUT_LATENCY_SECONDS
  private hasProbedPlatform = false

  constructor(private readonly latencyProbe: () => number | null = browserAudioOutputLatencySeconds) {}

  get visualLeadSeconds(): number {
    return adaptiveLipSyncLeadSeconds({
      frameSeconds: this.frameSeconds,
      audioOutputLatencySeconds: this.audioOutputLatencySeconds,
    })
  }

  probePlatformLatency(): void {
    if (this.hasProbedPlatform) return
    this.hasProbedPlatform = true
    const measured = this.latencyProbe()
    if (measured !== null && Number.isFinite(measured) && measured >= 0) {
      this.audioOutputLatencySeconds = measured
    }
  }

  sampleFrameDuration(seconds: number): void {
    // Ignore tab-resume and dropped-frame spikes; sustained cadence changes still converge quickly.
    if (!Number.isFinite(seconds) || seconds < 1 / 240 || seconds > 1 / 20) return
    this.frameSeconds += (seconds - this.frameSeconds) * 0.12
  }
}

/** Return the Rhubarb mouth pose active at an audio playhead, or the closed/rest pose. */
export function mouthCueAt(track: LipSyncTrack | null | undefined, seconds: number): MouthShape {
  if (!track || !Number.isFinite(seconds) || seconds < 0) return 'X'
  const cue = track.mouthCues.find(({ start, end }) => seconds >= start && seconds < end)
  return cue?.value ?? 'X'
}

/**
 * Sample a cue slightly ahead of the reported media playhead. HTMLAudio's currentTime is a
 * media timeline, while the sound reaches the device after its output buffer has been queued.
 * Keeping this correction in one bounded helper makes the compensation explicit and prevents
 * a noisy browser latency probe from jumping an actor several phonemes ahead.
 */
export function mouthCueAtWithLead(
  track: LipSyncTrack | null | undefined,
  seconds: number,
  leadSeconds: number,
): MouthShape {
  const lead = Number.isFinite(leadSeconds) ? clamp(leadSeconds, -0.1, MAX_VISUAL_LEAD_SECONDS) : 0
  return mouthCueAt(track, seconds + lead)
}
